export type StepUpAction = "archive" | "create";

export type StepUpSnapshot<Payload, Display> = {
  v: 1;
  action: StepUpAction;
  createdAt: number;
  payload: Payload;
  display?: Display;
};

export const STEP_UP_SNAPSHOT_TTL_MS = 10 * 60 * 1000;

const PENDING_KEY_PREFIX = "partners.stepup.pending:";

const keyFor = (scaUserId: string) => `${PENDING_KEY_PREFIX}${scaUserId}`;

const read = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const remove = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {}
};

const RETURNED_KEY = "partners.stepup.returned";

// Browser Back from Auth0 produces no redirect callback, so a missing marker means "went back", not "finished".
export const markStepUpReturned = (action: StepUpAction): void => {
  try {
    window.sessionStorage.setItem(RETURNED_KEY, action);
  } catch {}
};

export const hasStepUpReturned = (action: StepUpAction): boolean => {
  try {
    return window.sessionStorage.getItem(RETURNED_KEY) === action;
  } catch {
    return false;
  }
};

export const clearStepUpReturned = (): void => remove(RETURNED_KEY);

export const createStepUpSnapshotStore = <Payload, Display = unknown>(
  scaUserId: string,
  now: () => number = Date.now,
) => {
  const key = keyFor(scaUserId);

  const save = (entry: {
    action: StepUpAction;
    payload: Payload;
    display?: Display;
  }): boolean => {
    const snapshot: StepUpSnapshot<Payload, Display> = {
      v: 1,
      createdAt: now(),
      ...entry,
    };
    clearStepUpReturned();
    try {
      window.sessionStorage.setItem(key, JSON.stringify(snapshot));
      return true;
    } catch {
      return false;
    }
  };

  const peek = (
    action: StepUpAction,
  ): StepUpSnapshot<Payload, Display> | null => {
    const raw = read(key);
    if (raw === null) return null;

    let parsed: Partial<StepUpSnapshot<Payload, Display>> | null = null;
    try {
      parsed = JSON.parse(raw) as Partial<StepUpSnapshot<Payload, Display>>;
    } catch {
      parsed = null;
    }

    const age =
      parsed && typeof parsed.createdAt === "number"
        ? now() - parsed.createdAt
        : Number.NaN;
    const valid =
      parsed?.v === 1 &&
      "payload" in parsed &&
      age >= 0 &&
      age <= STEP_UP_SNAPSHOT_TTL_MS;

    if (!valid) {
      remove(key);
      return null;
    }
    if (parsed?.action !== action) return null;

    return parsed as StepUpSnapshot<Payload, Display>;
  };

  const consume = (
    action: StepUpAction,
  ): StepUpSnapshot<Payload, Display> | null => {
    const snapshot = peek(action);
    if (snapshot) remove(key);
    return snapshot;
  };

  const clear = () => remove(key);

  return { save, peek, consume, clear };
};
